import { useMemo } from 'react'
import { useWeeksInInterval } from './use-weeks-in-interval'
import { User, UserAssociation } from '../../api/users'
import { WorkerCounter } from '../../api/worker-counters'
import { Organization } from '../../api/organizations'
import {
  getWorkerAndOrganizationAssociation,
  getActiveContractualSchedule,
} from '../../utils/users'
import { useWorkersQuery } from '../../queries/users'
import { useWorkPeriodEventsQuery } from '../../queries/work-period-events'
import { useWeeksSummaryStatusQuery } from '../../queries/missions'
import { compareDocsById, getDocIdAsString } from '../../utils/documents'
import { PayrollFilters, PayrollWeekStatus } from '../../store/filters'
import {
  UserWeeksSummaryStatus,
  WeeksSummaryStatus,
} from '../../../../backend/src/services/resources/missions/missions.summary.service.types'
import { UserSummaries } from '../../api/missions'
import { usePayrollWorkersNavigation } from './use-payroll-workers-navigation'
import { WorkPeriodEvent } from '../../api/work-period-events'
import { sortBy } from 'lodash'

/**
 * Détermine le statut de clôture d'un worker basé sur ses semaines
 * @param weeksSummary - Résumé des semaines du worker
 * @returns Le statut de clôture du worker
 */
const getWorkerWeekStatus = (
  weeksSummary: UserWeeksSummaryStatus | undefined,
): PayrollWeekStatus => {
  if (!weeksSummary) {
    return 'needsValidation'
  }

  const { recordedWeeks, validatedWeeks, totalWeeks } = weeksSummary

  // Si toutes les semaines sont enregistrées (clôturées)
  if (recordedWeeks === totalWeeks) {
    return 'closed'
  }

  // Si toutes les semaines sont validées mais pas encore enregistrées
  if (validatedWeeks === totalWeeks) {
    return 'readyToClose'
  }

  // Sinon, il y a des semaines qui nécessitent une validation
  return 'needsValidation'
}

/**
 * Type pour un worker avec toutes ses données agrégées
 */
export interface PayrollWorker extends User {
  /** Association worker/organisation avec les heures hebdomadaires */
  association: UserAssociation | undefined
  /** Résumé des semaines pour ce worker */
  weeksSummary: UserWeeksSummaryStatus | undefined
  /** Compteurs du worker */
  workerCounters: WorkerCounter[] | undefined
  /** Heures hebdomadaires travaillées */
  weeklyHoursWorked: number | undefined
}

export interface PayrollWorkerWithWeeksSummary extends Omit<PayrollWorker, 'weeksSummary'> {
  weeksSummary: UserSummaries
}

/**
 * Hook pour récupérer et agréger toutes les données des workers pour la paie
 * Retourne un tableau unifié où chaque worker contient toutes ses données
 */
export const usePayrollWorkers = (
  currentOrganization: Organization,
  filters: PayrollFilters,
  currentWorkerId: string | undefined,
): {
  payrollWorkers: PayrollWorker[]
  weeksSummaryStatus: WeeksSummaryStatus | undefined
  allWorkPeriodEvents: WorkPeriodEvent[]
  weeks: Date[] | undefined
  currentWorkerWithWeeksSummary: PayrollWorkerWithWeeksSummary | undefined
  isLoading: boolean
  navigableWorkers: PayrollWorker[]
  currentIndex: number
  hasPrev: boolean
  hasNext: boolean
  currentWorker: PayrollWorker | undefined
  currentNavigableWorker: PayrollWorker | undefined
  /**
   * Suggested current worker id after applying navigation constraints and filters
   */
  resolvedCurrentWorkerId: string | undefined
  /**
   * Convenience helpers that return the id to navigate to (no side effects)
   */
  goPrev: () => string | undefined
  goNext: () => string | undefined
} => {
  // Query pour récupérer tous les workers de l'organisation
  const workersQuery = useWorkersQuery({
    // Narrow the literal type to match MissionContractModel
    contractModel: ['twoParties' as any],
    service: filters?.service,
    'start<': filters?.end,
    'end>': filters?.start,
    user: filters?.user,
    jobTitle: filters?.jobTitle,
  })

  // Query pour récupérer tous les événements de période de travail
  const allWorkPeriodEvents = useWorkPeriodEventsQuery()

  // Query pour récupérer le statut des semaines
  const weeksSummaryStatusQuery = useWeeksSummaryStatusQuery(
    {
      from: filters?.start as Date,
      to: filters?.end as Date,
      user: (workersQuery.data || []).map(getDocIdAsString),
      contractModel: ['twoParties' as any],
    },
    {
      enabled: Boolean(
        filters?.start && filters?.end && workersQuery.data?.[0] && !workersQuery.isLoading,
      ),
    },
  )

  // Calcul des semaines dans la période sélectionnée (hook dédié)
  const weeks = useWeeksInInterval(
    filters?.start as Date | undefined,
    filters?.end as Date | undefined,
  )

  // Tableau unifié des workers avec toutes leurs données agrégées
  const payrollWorkers = useMemo((): PayrollWorker[] => {
    if (!workersQuery.data || !weeksSummaryStatusQuery.data) {
      return []
    }

    let res = sortBy(
      workersQuery.data.map(worker => {
        const association = getWorkerAndOrganizationAssociation(worker, currentOrganization)
        const weeksSummaryStatus = weeksSummaryStatusQuery.data.users.find(summary =>
          compareDocsById(summary.user, worker),
        )

        return {
          ...worker,
          association,
          weeksSummary: weeksSummaryStatus,
          workerCounters: undefined, // Sera chargé individuellement si nécessaire
          weeklyHoursWorked: association
            ? getActiveContractualSchedule(association)?.weeklyHoursWorked
            : undefined,
        }
      }),
      'lastName',
    )

    // Appliquer les filtres de statut des semaines
    const weekStatuses = filters.weekStatuses
    res = !weekStatuses?.length
      ? res
      : res.filter(worker => {
          const workerStatus = getWorkerWeekStatus(worker.weeksSummary)
          return weekStatuses.includes(workerStatus)
        })

    return res
  }, [workersQuery.data, weeksSummaryStatusQuery.data, currentOrganization, filters.weekStatuses])

  // Navigation extraite dans un hook dédié
  const {
    navigableWorkers,
    currentIndex,
    hasPrev,
    hasNext,
    currentWorker,
    currentNavigableWorker,
    resolvedCurrentWorkerId,
    goPrev,
    goNext,
  } = usePayrollWorkersNavigation<PayrollWorker>(payrollWorkers, currentWorkerId)

  // Deprecated path: kept for backward compatibility during migration (returns undefined)
  const currentWorkerWithWeeksSummary = undefined as unknown as
    | PayrollWorkerWithWeeksSummary
    | undefined

  return {
    // Données principales
    payrollWorkers,
    allWorkPeriodEvents: allWorkPeriodEvents.data || [],
    weeks,
    weeksSummaryStatus: weeksSummaryStatusQuery.data,
    currentWorkerWithWeeksSummary,
    // États de chargement
    isLoading: workersQuery.isLoading || allWorkPeriodEvents.isLoading,
    // Navigation
    navigableWorkers,
    currentIndex,
    hasPrev,
    hasNext,
    currentWorker,
    currentNavigableWorker,
    resolvedCurrentWorkerId,
    goPrev,
    goNext,
  }
}
